import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Search,
  ChevronLeft,
  MapPin,
  Star,
  Filter,
  X,
  AlertCircle,
  Shield,
  Clock,
  GraduationCap,
} from "lucide-react";
import { AdminContext } from "../../../context/AdminContext";
import { AppContext } from "../../../context/AppContext";
import Pagination from "../../../components/Pagination";
import { toast } from "react-toastify";

const ReplaceCaregiver = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const selectedBooking =
    location.state?.selectedBooking || location.state?.selectedAppointment;
  const serviceCategoryId = selectedBooking?.serviceCategoryId;
  const category =
    selectedBooking?.category || selectedBooking?.careGiverData?.category;

  const { getCaregiversByCategory, replaceCaregiver } =
    React.useContext(AdminContext);
  const { currency } = React.useContext(AppContext);

  const [caregivers, setCaregivers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchInput, setSearchInput] = useState(""); // Separate input state
  const [subcityFilter, setSubcityFilter] = useState("All");
  const [sortOrder, setSortOrder] = useState("low-high");
  const [showFilters, setShowFilters] = useState(true);

  // Replacement reason modal
  const [selectedCaregiverId, setSelectedCaregiverId] = useState(null);
  const [selectedCaregiverName, setSelectedCaregiverName] = useState("");
  const [replacementReason, setReplacementReason] = useState("");
  const [showReasonModal, setShowReasonModal] = useState(false);

  // Loading states
  const [loading, setLoading] = useState(true);
  const [replacing, setReplacing] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);

  // Search handlers
  const handleSearch = () => {
    setSearchTerm(searchInput);
    setCurrentPage(1);
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const handleClearSearch = () => {
    setSearchInput("");
    setSearchTerm("");
    setCurrentPage(1);
  };

  useEffect(() => {
    if (!selectedBooking) {
      toast.error("No booking selected");
      navigate("/bookings");
      return;
    }

    const fetchCaregivers = async () => {
      setLoading(true);
      try {
        if (!serviceCategoryId) {
          toast.error("Invalid booking category. Cannot replace caregiver.");
          navigate("/bookings");
          return;
        }

        const data = await getCaregiversByCategory(serviceCategoryId);

        const careGiverId =
          selectedBooking.careGiverId || selectedBooking.careGiverData?.id;

        const { slotDate, slotTime } = selectedBooking;

        // Filter: exclude current caregiver, must be onboarded, slot must be free
        const filteredData = data
          .filter(
            (c) =>
              c._id !== careGiverId &&
              c.vetting_status === "Onboarded" &&
              c.available === true &&
              c.currentJob == null &&
              c.currentBooking == null,
          )
          .map((c) => ({
            id: c._id, // Backend returns _id, map it to id
            name: c.name,
            avatar: c.image || "/default-avatar.png",
            phone: c.phoneNumber || "N/A",
            email: c.email || "N/A",
            serviceType: selectedBooking.category,
            subcity: c.subcity || "Not specified",
            fees: c.fees || 0,
            rating: c.rating || 0,
            languages: c.languages || [],
            education: c.education || "N/A",
            experience: c.experience || "N/A",
            currentJob: c.currentJob,
            vetting_status: c.vetting_status,
            onboarded_date: c.onboarded_date,
            skills: c.skills || [],
            certifications: c.certifications || [],
          }));

        setCaregivers(filteredData);

        if (filteredData.length === 0) {
          toast.info(
            "No available onboarded caregivers found for this timeslot",
          );
        }
      } catch (error) {
        console.error("Error fetching caregivers:", error);
        toast.error("Failed to load available caregivers");
      } finally {
        setLoading(false);
      }
    };

    fetchCaregivers();
  }, [selectedBooking, getCaregiversByCategory, navigate]);

  // Filter & Sort Logic
  const filteredCaregivers = caregivers
    .filter((c) => {
      const matchesSearch =
        c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.phone.includes(searchTerm) ||
        c.email.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesSubcity =
        subcityFilter === "All" || c.subcity === subcityFilter;
      return matchesSearch && matchesSubcity;
    })
    .sort((a, b) => {
      return sortOrder === "low-high" ? a.fees - b.fees : b.fees - a.fees;
    });

  // Pagination Logic
  const totalPages = Math.ceil(filteredCaregivers.length / itemsPerPage);
  const paginatedCaregivers = filteredCaregivers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, subcityFilter, sortOrder, itemsPerPage]);

  //  Replacement Flow with Audit Trail
  const openReplaceModal = (caregiverId, caregiverName) => {
    setSelectedCaregiverId(caregiverId);
    setSelectedCaregiverName(caregiverName);
    setReplacementReason("");
    setShowReasonModal(true);
  };

  const confirmReplace = async () => {
    if (replacementReason.trim().length < 5) {
      toast.error("Replacement reason must be at least 5 characters.");
      return;
    }

    if (replacementReason.trim().length > 500) {
      toast.error("Reason too long (max 500 characters)");
      return;
    }

    setReplacing(true);
    try {
      await replaceCaregiver(
        selectedBooking.id,
        selectedCaregiverId,
        replacementReason.trim(),
      );
      setShowReasonModal(false);
      navigate("/bookings");
    } catch (error) {
      console.error("Replacement error:", error);
    } finally {
      setReplacing(false);
    }
  };

  // Available subcities from data
  const availableSubcities = [
    "All",
    ...new Set(caregivers.map((c) => c.subcity).filter(Boolean)),
  ];

  return (
    <div className="animate-in fade-in slide-in-from-right-4 duration-500 pb-12">
      {/*  Replacement Reason Modal */}
      {showReasonModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-card dark:bg-dark-surface rounded-2xl w-full max-w-lg shadow-2xl border border-gray-200 dark:border-dark-border">
            {/* Modal Header */}
            <div className="p-6 border-b border-gray-100 dark:border-dark-border">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <AlertCircle className="text-primary" size={20} />
                  <div>
                    <h2 className="font-bold text-lg text-text dark:text-dark-text">
                      Document Replacement Reason
                    </h2>
                    <p className="text-sm text-gray-500 dark:text-dark-muted mt-1">
                      This action will be permanently recorded
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowReasonModal(false)}
                  disabled={replacing}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              <div className="mb-4">
                <label className="block text-sm font-medium text-text dark:text-dark-text mb-2">
                  Replacement Reason *
                  <span className="text-xs text-gray-500 dark:text-dark-muted ml-2">
                    (Minimum 5 characters)
                  </span>
                </label>
                <textarea
                  value={replacementReason}
                  onChange={(e) => setReplacementReason(e.target.value)}
                  placeholder="Explain why you are replacing the caregiver. Be specific and professional."
                  className="w-full border border-gray-300 dark:border-dark-border rounded-xl p-4 min-h-[120px] bg-white dark:bg-dark-background text-text dark:text-dark-text placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none resize-none"
                  disabled={replacing}
                  autoFocus
                />
                <div className="flex justify-between mt-2">
                  <span className="text-xs text-gray-500 dark:text-dark-muted">
                    {replacementReason.length}/500 characters
                  </span>
                  {replacementReason.length < 5 && (
                    <span className="text-xs text-red-500">
                      Minimum 5 characters required
                    </span>
                  )}
                </div>
              </div>

              <div className="text-sm text-gray-600 dark:text-dark-muted">
                <p className="font-medium mb-1">You are replacing with:</p>
                <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <span className="font-bold text-primary">
                      {selectedCaregiverName.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium text-text dark:text-dark-text">
                      {selectedCaregiverName}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-dark-muted">
                      Booking #{id} • {category}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-6 border-t border-gray-100 dark:border-dark-border flex gap-3">
              <button
                onClick={() => setShowReasonModal(false)}
                disabled={replacing}
                className="flex-1 py-3 px-4 border border-gray-300 dark:border-dark-border text-gray-700 dark:text-dark-text font-medium rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmReplace}
                disabled={
                  replacing ||
                  replacementReason.trim().length < 5 ||
                  replacementReason.trim().length > 500
                }
                className="flex-1 py-3 px-4 bg-primary hover:bg-primary/90 text-white font-bold rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {replacing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Processing...
                  </>
                ) : (
                  <>Confirm Replacement</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate("/bookings")}
            className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-gray-700 dark:text-dark-text"
          >
            <ChevronLeft size={24} />
          </button>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-text dark:text-dark-text">
              Replace Caregiver
            </h1>
            <div className="flex flex-wrap items-center gap-3 mt-2">
              <p className="text-gray-600 dark:text-dark-muted">
                Booking #{id} • {category}
              </p>
            </div>
          </div>
        </div>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="bg-card dark:bg-dark-surface text-gray-700 dark:text-dark-text px-4 py-2.5 rounded-xl border border-gray-200 dark:border-dark-border text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors shadow-sm flex items-center gap-2 self-start md:self-auto"
        >
          <Filter size={16} /> {showFilters ? "Hide Filters" : "Show Filters"}
        </button>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="bg-card dark:bg-dark-surface p-4 rounded-2xl shadow-sm border border-gray-200 dark:border-dark-border mb-6 grid grid-cols-1 md:grid-cols-4 gap-4 animate-in fade-in slide-in-from-top-2 duration-200">
          <div className="md:col-span-2 relative">
            <Search
              className="absolute left-3.5 top-1/2 -translate-y-1/2 text-primary cursor-pointer hover:text-primary/80 transition-colors"
              size={18}
              onClick={handleSearch}
            />
            <input
              type="text"
              placeholder="Search by name, phone, or email... (Press Enter)"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyPress={handleSearchKeyPress}
              className="w-full pl-11 pr-10 py-2.5 rounded-xl bg-gray-50 dark:bg-dark-background border-none outline-none focus:ring-2 focus:ring-primary/20 text-text dark:text-dark-text placeholder-gray-500 dark:placeholder-gray-400 text-sm"
            />
            {searchInput && (
              <X
                className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 cursor-pointer hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                size={16}
                onClick={handleClearSearch}
              />
            )}
          </div>
          <div className="relative">
            <MapPin
              className="absolute left-3.5 top-1/2 -translate-y-1/2 text-primary pointer-events-none"
              size={16}
            />
            <select
              value={subcityFilter}
              onChange={(e) => setSubcityFilter(e.target.value)}
              className="w-full appearance-none pl-10 pr-10 py-2.5 rounded-xl bg-gray-50 dark:bg-dark-background border-none outline-none text-text dark:text-dark-text text-sm font-medium cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              {availableSubcities.map((subcity) => (
                <option key={subcity} value={subcity}>
                  {subcity}
                </option>
              ))}
            </select>
          </div>
          <div className="relative">
            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              className="w-full appearance-none pl-4 pr-10 py-2.5 rounded-xl bg-gray-50 dark:bg-dark-background border-none outline-none text-text dark:text-dark-text text-sm font-medium cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <option value="low-high">Fee: Low to High</option>
              <option value="high-low">Fee: High to Low</option>
              <option value="rating">Highest Rating</option>
            </select>
          </div>
        </div>
      )}

      {/* Stats Bar */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-card dark:bg-dark-surface p-4 rounded-2xl border border-gray-200 dark:border-dark-border">
          <div className="flex items-center gap-3">
            <Shield className="text-primary" size={18} />
            <div>
              <p className="text-sm text-gray-500 dark:text-dark-muted">
                Onboarded Only
              </p>
              <p className="text-xl font-bold text-text dark:text-dark-text">
                {caregivers.length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-card dark:bg-dark-surface p-4 rounded-2xl border border-gray-200 dark:border-dark-border">
          <div className="flex items-center gap-3">
            <Clock className="text-primary" size={18} />
            <div>
              <p className="text-sm text-gray-500 dark:text-dark-muted">Slot</p>
              <p className="text-lg font-bold text-text dark:text-dark-text">
                {selectedBooking?.slotDate} • {selectedBooking?.slotTime}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-card dark:bg-dark-surface p-4 rounded-2xl border border-gray-200 dark:border-dark-border">
          <div className="flex items-center gap-3">
            <GraduationCap className="text-primary" size={18} />
            <div>
              <p className="text-sm text-gray-500 dark:text-dark-muted">
                Category
              </p>
              <p className="text-lg font-bold text-text dark:text-dark-text capitalize">
                {category}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Caregiver List */}
      <div className="bg-card dark:bg-dark-surface rounded-2xl shadow-sm border border-gray-200 dark:border-dark-border overflow-hidden flex flex-col">
        <div className="overflow-x-auto">
          <table className="w-full whitespace-nowrap">
            <thead>
              <tr className="border-b border-slate-700 bg-slate-900">
                <th className="px-6 py-4 text-left text-xs font-bold text-white/70 uppercase tracking-wider">
                  Caregiver
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-white/70 uppercase tracking-wider">
                  Location & Skills
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-white/70 uppercase tracking-wider">
                  Rating
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-white/70 uppercase tracking-wider">
                  Experience
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-white/70 uppercase tracking-wider">
                  Fee
                </th>
                <th className="px-6 py-4 text-right text-xs font-bold text-white/70 uppercase tracking-wider">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                      <p className="text-gray-500 dark:text-dark-muted">
                        Loading available caregivers...
                      </p>
                    </div>
                  </td>
                </tr>
              ) : paginatedCaregivers.length > 0 ? (
                paginatedCaregivers.map((c) => (
                  <tr
                    key={c.id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors group"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-4">
                        <img
                          src={c.avatar}
                          alt={c.name}
                          className="w-12 h-12 rounded-full object-cover bg-gray-100 dark:bg-gray-700 border-2 border-white dark:border-gray-800 shadow-sm"
                        />
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-bold text-text dark:text-dark-text">
                              {c.name}
                            </p>
                            <span className="px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 text-xs font-medium rounded-full">
                              Onboarded
                            </span>
                          </div>
                          <p className="text-xs text-gray-500 dark:text-dark-muted mt-1">
                            {c.phone} • {c.email}
                          </p>
                          <div className="flex gap-1 mt-2 flex-wrap">
                            {c.languages.slice(0, 3).map((lang, idx) => (
                              <span
                                key={`${c.id}-lang-${idx}`}
                                className="text-[10px] bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded text-gray-700 dark:text-gray-300"
                              >
                                {lang}
                              </span>
                            ))}
                            {c.languages.length > 3 && (
                              <span className="text-[10px] text-gray-500 dark:text-dark-muted px-1">
                                +{c.languages.length - 3}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-dark-muted mb-2">
                        <MapPin size={14} className="text-primary" />
                        {c.subcity}
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {c.skills.slice(0, 2).map((skill, idx) => (
                          <span
                            key={`${c.id}-skill-${idx}`}
                            className="text-xs bg-primary/10 text-primary px-2 py-1 rounded"
                          >
                            {skill}
                          </span>
                        ))}
                        {c.skills.length > 2 && (
                          <span className="text-xs text-gray-500 dark:text-dark-muted px-1">
                            +{c.skills.length - 2}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1.5 bg-amber-50 dark:bg-amber-900/10 px-3 py-1.5 rounded-lg w-fit">
                        <Star
                          size={14}
                          className="text-amber-500 fill-amber-500"
                        />
                        <span className="text-sm font-bold text-amber-700 dark:text-amber-500">
                          {c.rating.toFixed(1)}
                        </span>
                        <span className="text-xs text-amber-600 dark:text-amber-400">
                          /5.0
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm font-medium text-text dark:text-dark-text">
                        {c.experience} yrs
                      </p>
                      <p className="text-xs text-gray-500 dark:text-dark-muted">
                        {c.education}
                      </p>
                      {c.certifications.length > 0 && (
                        <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                          {c.certifications.length} certification
                          {c.certifications.length !== 1 ? "s" : ""}
                        </p>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-lg font-bold text-text dark:text-dark-text">
                        {currency} {c.fees.toLocaleString()}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-dark-muted">
                        Monthly rate
                      </p>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={() => openReplaceModal(c.id, c.name)}
                        disabled={!!c.currentJob}
                        className="px-5 py-2.5 bg-primary hover:bg-primary/90 text-white rounded-xl text-sm font-bold transition-all disabled:bg-gray-300 disabled:hover:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
                      >
                        Select & Document
                      </button>
                      {c.currentJob && (
                        <p className="text-xs text-red-500 dark:text-red-400 mt-1">
                          Currently assigned to another job
                        </p>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={6}
                    className="px-6 py-16 text-center text-gray-500 dark:text-dark-muted"
                  >
                    <div className="flex flex-col items-center gap-3">
                      <Shield
                        size={48}
                        className="text-gray-300 dark:text-gray-600"
                      />
                      <div>
                        <p className="font-medium text-text dark:text-dark-text">
                          No available caregivers found
                        </p>
                        <p className="text-sm mt-1">
                          All onboarded caregivers are either busy or not
                          available for this timeslot
                        </p>
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!loading && paginatedCaregivers.length > 0 && (
          <div className="border-t border-gray-100 dark:border-dark-border bg-gray-50/50 dark:bg-gray-800/30 p-2">
            <div className="flex items-center justify-between px-4 py-2">
              <p className="text-sm text-gray-500 dark:text-dark-muted">
                Showing{" "}
                <span className="font-medium text-text dark:text-dark-text">
                  {Math.min(
                    filteredCaregivers.length,
                    (currentPage - 1) * itemsPerPage + 1,
                  )}
                </span>{" "}
                to{" "}
                <span className="font-medium text-text dark:text-dark-text">
                  {Math.min(
                    filteredCaregivers.length,
                    currentPage * itemsPerPage,
                  )}
                </span>{" "}
                of{" "}
                <span className="font-medium text-text dark:text-dark-text">
                  {filteredCaregivers.length}
                </span>{" "}
                available caregivers
              </p>
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                itemsPerPage={itemsPerPage}
                onItemsPerPageChange={setItemsPerPage}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReplaceCaregiver;
